import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { SUPPORTED_COUNTRIES } from '@/constants';
import Swal from 'sweetalert2';

export function useProfileValidation() {
  const router = useRouter();
  const authStore = useAuthStore();
  
  const isCheckingProfile = ref(false);
  const missingData = ref({
    phone: false,
    shirt_size: false,
    birthdate: false
  });

  const getCountryId = (countryName) => {
    return SUPPORTED_COUNTRIES[countryName] || null;
  };

  const changeUserCountry = async (countryName) => {
    const countryId = getCountryId(countryName);
    if (!countryId) {
      throw new Error(`No se pudo encontrar el ID para el país: ${countryName}`);
    }

    try {
      await authStore.updateCountry(countryId);
      return true;
    } catch (error) {
      console.error('Error al cambiar país:', error);
      throw error;
    }
  };

  const handleCountryError = async (errorData, eventId, registerCallback) => {
    const requiredCountry = errorData.required_country;
    
    const confirmCountryChange = await Swal.fire({
      title: "¿Cambiar país de perfil?",
      html: `
        <div class="text-left">
          <p class="mb-4">${errorData.message}</p>
          <p class="text-gray-600 text-sm">¿Estás seguro que deseas cambiar tu país de perfil a <strong>${requiredCountry}</strong>?</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cambiar país",
      cancelButtonText: "Cancelar"
    });

    if (!confirmCountryChange.isConfirmed) return;

    try {
      Swal.fire({
        title: 'Cambiando país...',
        text: 'Por favor espera mientras actualizamos tu perfil',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await changeUserCountry(requiredCountry);

      const continueRegistration = await Swal.fire({
        title: "¡País cambiado exitosamente!",
        html: `
          <div class="text-left">
            <p class="mb-4">Tu país de perfil ha sido actualizado a <strong>${requiredCountry}</strong>.</p>
            <p class="text-gray-600 text-sm">¿Deseas continuar con el registro al evento?</p>
          </div>
        `,
        icon: "success",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, registrarme",
        cancelButtonText: "Más tarde"
      });

      if (continueRegistration.isConfirmed && registerCallback) {
        await registerCallback(eventId);
      }

    } catch (error) {
      console.error('Error al cambiar país:', error);
      Swal.fire({
        title: 'Error al cambiar país',
        text: error.response?.data?.message || 'No se pudo cambiar el país. Inténtalo de nuevo.',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  };

  const checkProfileComplete = async () => {
    try {
      isCheckingProfile.value = true;
      const response = await authStore.fetchUserData();
      const user = response.user;
      
      const missing = {
        phone: !user.phone,
        shirt_size: !user.shirt_size,
        birthdate: !user.birthdate
      };
      
      missingData.value = missing;
      
      const isComplete = !missing.phone && !missing.shirt_size && !missing.birthdate;
      
      return isComplete;
    } catch (error) {
      console.error('Error al verificar perfil:', error);
      missingData.value = { phone: true, shirt_size: true, birthdate: true };
      return false;
    } finally {
      isCheckingProfile.value = false;
    }
  };

  const missingFields = computed(() => {
    const fields = [];
    if (missingData.value.phone) fields.push('Teléfono');
    if (missingData.value.shirt_size) fields.push('Talla de camisa');
    if (missingData.value.birthdate) fields.push('Fecha de cumpleaños');
    return fields;
  });

  // Mostrar modal de perfil incompleto usando SweetAlert
  const showProfileIncompleteModal = () => {
    return Swal.fire({
      title: 'Perfil Incompleto',
      html: `
        <div class="text-left">
          <p class="mb-4">Para completar tu registro en este evento, necesitas completar la siguiente información en tu perfil:</p>
          <ul class="list-disc list-inside space-y-2 mb-4 text-red-600">
            ${missingFields.value.map(field => `<li>${field}</li>`).join('')}
          </ul>
          <p class="text-gray-600 text-sm">Esta información es necesaria para poder procesar tu registro y entregarte el kit del evento.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Completar Perfil',
      cancelButtonText: 'Cancelar'
    });
  };

  const validateProfileBeforeRegistration = async (callback) => {
    const isComplete = await checkProfileComplete();
    
    if (!isComplete) {
      const result = await showProfileIncompleteModal();
      if (result.isConfirmed) {
        router.push('/dashboard/profile/overview');
      }
      return false;
    }
    
    if (callback && typeof callback === 'function') {
      callback();
    }
    
    return true;
  };

  const validateEventCountry = async (event) => {
    try {
      const response = await authStore.fetchUserData();
      const user = response.user;
      
      if (!user.country || !event.country) {
        return true;
      }
      
      const userCountry = user.country.name;
      const eventCountry = event.country.name;
      
      return userCountry === eventCountry;
    } catch (error) {
      console.error('Error al validar país del evento:', error);
      return true;
    }
  };

  const showEventCountryMismatchModal = (eventCountry) => {
    return Swal.fire({
      title: 'Evento no disponible',
      html: `
        <div class="text-left">
          <p class="mb-4">Este evento está disponible únicamente para usuarios de <strong>${eventCountry}</strong>.</p>
          <p class="text-gray-600 text-sm">Solo puedes registrarte en eventos de tu país de residencia.</p>
        </div>
      `,
      icon: 'info',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Entendido'
    });
  };

  const validateEventCountryBeforeRegistration = async (event, callback) => {
    const isValidCountry = await validateEventCountry(event);
    
    if (!isValidCountry) {
      await showEventCountryMismatchModal(event.country.name);
      return false;
    }
    
    if (callback && typeof callback === 'function') {
      await callback();
    }
    
    return true;
  };

  return {
    isCheckingProfile,
    missingData,
    missingFields,
    checkProfileComplete,
    showProfileIncompleteModal,
    validateProfileBeforeRegistration,
    handleCountryError,
    changeUserCountry,
    validateEventCountry,
    showEventCountryMismatchModal,
    validateEventCountryBeforeRegistration
  };
} 